import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Typography, TextField, Button, Checkbox, FormControlLabel, Alert, Stack, LinearProgress, Snackbar, Collapse, Link, Box, SvgIcon } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../services/api';

const CareerVerifyPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const isPreview = !token;
  const [loading, setLoading] = React.useState(true);
  const [valid, setValid] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [rodo, setRodo] = React.useState(false);
  const [jobOffersConsent, setJobOffersConsent] = React.useState(false);
  const [cv, setCv] = React.useState<File | null>(null);
  const [cover, setCover] = React.useState<File | null>(null);
  const [ok, setOk] = React.useState(false);
  const [positionTitle, setPositionTitle] = React.useState<string>('');
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
  const siteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY as string) || '';
  const [submitting, setSubmitting] = React.useState(false);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [showRodoDetails, setShowRodoDetails] = React.useState(false);
  const [showJobOffersDetails, setShowJobOffersDetails] = React.useState(false);
  const grey600 = '#5F6368';

  const UncheckedIcon = (props: any) => (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ width: 24, height: 24 }}>
      <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2Z" />
    </SvgIcon>
  );
  const CheckedIcon = (props: any) => (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ width: 24, height: 24 }}>
      <rect x="3" y="3" width="18" height="18" rx="4" ry="4" fill={grey600} />
      <path d="M10 17 5 12.5l1.41-1.41L10 14l7.59-7.59L19 7 10 17Z" fill="#FFFFFF" />
    </SvgIcon>
  );

  React.useEffect(() => {
    let mounted = true;
    if (isPreview) {
      setValid(true);
      setLoading(false);
      return () => { mounted = false; };
    }
    (async () => {
      try {
        const resp = await api.get('/recruit/public/verify-email', { params: { token } });
        if (!mounted) return;
        setValid(Boolean(resp.data?.success));
        try { setPositionTitle(resp.data?.data?.offer_title || ''); } catch {}
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Token nieprawidłowy lub wygasł');
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token, isPreview]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setOk(false);
    try {
      if (isPreview) throw new Error('Podgląd UI – w tym trybie wysyłka jest wyłączona');
      if (!cv) throw new Error('Załącz plik CV');
      if (cv.size > 10 * 1024 * 1024) throw new Error('Maksymalny rozmiar CV to 10 MB');
      if (cover && cover.size > 10 * 1024 * 1024) throw new Error('Maksymalny rozmiar listu motywacyjnego to 10 MB');
      if (!siteKey) throw new Error('Brak konfiguracji reCAPTCHA');
      if (!captchaToken) throw new Error('Zaznacz reCAPTCHA');
      setSubmitting(true);
      setSnackOpen(true);
      const form = new FormData();
      form.append('token', token);
      form.append('rodo_consent', rodo ? '1' : '0');
      form.append('job_offers_consent', jobOffersConsent ? '1' : '0');
      form.append('cv_file', cv);
      if (cover) form.append('cover_letter_file', cover);
      form.append('captcha_token', captchaToken);
      const resp = await api.post('/recruit/public/applications/complete', form);
      if (resp.data?.success) {
        setOk(true);
        setErr(null);
        // Backend wysyła e‑mail z linkiem do testu – brak przekierowania
      } else {
        throw new Error(resp.data?.message || 'Błąd wysyłki');
      }
    } catch (e: any) {
      const apiMsg = e?.response?.data?.message || e?.message;
      setErr(apiMsg || 'Nie udało się wysłać formularza. Sprawdź wymagane pola.');
      setOk(false);
    }
    finally {
      setSubmitting(false);
      setSnackOpen(false);
    }
  };

  if (loading) return <Container maxWidth="sm" sx={{ py: 4 }}><Typography>Ładowanie...</Typography></Container>;
  if (!valid) return <Container maxWidth="sm" sx={{ py: 4 }}><Alert severity="error">{err || 'Token nieprawidłowy lub wygasł'}</Alert></Container>;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:8 }}>
        <Typography component="span" variant="h5" sx={{ fontWeight: 800, color: '#c53030', letterSpacing: '2px', textTransform: 'uppercase' }}>Unitresco</Typography>
        <Typography component="span" variant="h5" gutterBottom>Formularz zgłoszeniowy{positionTitle ? ` — ${positionTitle}` : ''}</Typography>
      </div>
      <form onSubmit={onSubmit}>
        <Stack spacing={2}>
          {isPreview && <Alert severity="info">Tryb podglądu — brak tokenu. Wysyłka formularza jest wyłączona.</Alert>}
          {err && <Alert severity="error">{err}</Alert>}
          {ok && <Alert severity="success">Dziękujemy! Wyślemy link do testu (ważny 7 dni) na Twój e‑mail.</Alert>}
          <Button variant="outlined" component="label">Wybierz CV<input hidden multiple type="file" accept="application/pdf,.pdf,.doc,.docx" onChange={(e)=>{
            const files = Array.from(e.target.files || []);
            setCv(files[0] || null);
            setCover(files[1] || null);
            setErr(null); setOk(false);
          }} /></Button>
          <Typography variant="body2" color="text.secondary">{cv ? cv.name : 'Nie wybrano pliku CV'}</Typography>
          {cover && <Typography variant="body2" color="text.secondary">{cover.name}</Typography>}
          <Typography variant="caption" color="text.secondary">* Opcjonalnie możesz dołączyć list motywacyjny jako drugi plik.</Typography>
          <Box sx={{ display:'flex', alignItems:'flex-start', gap: 1 }}></Box>
          <Box sx={{ pl: 5, display:'grid', gridTemplateColumns: '28px 1fr', columnGap: 1, alignItems:'flex-start' }}>
            <Box>
              <Checkbox checked={rodo} onChange={(e)=>setRodo(e.target.checked)}
                sx={{ mt: '-5px', color: grey600, '&.Mui-checked': { color: grey600 } }}
                icon={<UncheckedIcon htmlColor={grey600} />}
                checkedIcon={<CheckedIcon htmlColor={grey600} />}
              />
            </Box>
            <Box sx={{ fontSize: 13, lineHeight: 1.5 }}>Wyrażam zgodę na przetwarzanie moich danych osobowych w celu przeprowadzenia bieżącego procesu rekrutacyjnego</Box>
          </Box>
          <Link component="button" type="button" onClick={()=>setShowRodoDetails(v=>!v)} underline="hover" sx={{ fontSize: 12.5, pl: 5, mt: 1 }}>
            {showRodoDetails ? 'Ukryj treść oświadczenia' : 'Pokaż treść oświadczenia'}
          </Link>
          <Collapse in={showRodoDetails} timeout="auto" unmountOnExit>
            <Typography variant="body2" sx={{ pl: 5, mt: 0.5 }}>
              Oświadczam, że zapoznałem/am się z Polityką Ochrony Danych Osobowych UNITRESCO sp. z o.o. dostępnej na stronie <a href="https://app.unitresco.com/documents" target="_blank" rel="noreferrer">app.unitresco.com/documents</a> i wyrażam zgodę na przetwarzanie moich danych osobowych zawartych w przesyłanym CV w celu przeprowadzenia procesu rekrutacyjnego.<br/>
              Zostałem/am poinformowany/a, że:
              <ul style={{ marginTop: 6, marginBottom: 0 }}>
                <li>Administratorem moich danych osobowych jest UNITRESCO sp. z o.o. z siedzibą przy ul. Żurawia 71, 15-540 Białystok</li>
                <li>Moje dane będą przetwarzane wyłącznie w celu przeprowadzenia procesu rekrutacyjnego</li>
                <li>Podanie danych jest dobrowolne, ale niezbędne do udziału w rekrutacji</li>
                <li>Przysługuje mi prawo dostępu, sprostowania, usunięcia, ograniczenia przetwarzania i przenoszenia danych</li>
                <li>Mogę w dowolnym momencie wycofać zgodę bez wpływu na zgodność z prawem przetwarzania przed jej wycofaniem</li>
                <li>Przysługuje mi prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych</li>
                <li>W sprawach związanych z ochroną danych mogą kontaktować się pod adresem: iod@unitresco.com</li>
              </ul>
            </Typography>
          </Collapse>
          <div>
            <Box sx={{ display:'flex', alignItems:'flex-start', gap: 1 }}></Box>
            <Box sx={{ pl: 5, display:'grid', gridTemplateColumns: '28px 1fr', columnGap: 1, alignItems:'flex-start' }}>
              <Box>
                <Checkbox checked={jobOffersConsent} onChange={(e)=>setJobOffersConsent(e.target.checked)}
                  sx={{ mt: '-5px', color: grey600, '&.Mui-checked': { color: grey600 } }}
                  icon={<UncheckedIcon htmlColor={grey600} />}
                  checkedIcon={<CheckedIcon htmlColor={grey600} />}
                />
              </Box>
              <Box sx={{ fontSize: 13, lineHeight: 1.5 }}>Wyrażam zgodę na przechowywanie moich danych osobowych przez 3 lata i otrzymywanie informacji o przyszłych ofertach pracy</Box>
            </Box>
            <Link component="button" type="button" onClick={()=>setShowJobOffersDetails(v=>!v)} underline="hover" sx={{ fontSize: 12.5, pl: 5, mt: 1 }}>
              {showJobOffersDetails ? 'Ukryj treść oświadczenia' : 'Pokaż treść oświadczenia'}
            </Link>
            <Collapse in={showJobOffersDetails} timeout="auto" unmountOnExit>
              <Typography variant="body2" sx={{ pl: 5, mt: 0.5 }}>
                Wyrażam zgodę na otrzymywanie przez okres trzech lat informacji dotyczących propozycji zatrudnienia w ramach rekrutacji prowadzonej przez UNITRESCO sp. z o.o. oraz na przechowywanie moich danych osobowych (w tym CV) w bazie danych UNITRESCO sp. z o.o. przez okres 3 lat w tym celu.<br/>
                Zostałem/am poinformowany/a, że:
                <ul style={{ marginTop: 6, marginBottom: 0 }}>
                  <li>Administratorem moich danych osobowych jest UNITRESCO sp. z o.o. z siedzibą przy ul. Żurawia 71, 15-540 Białystok</li>
                  <li>Moje dane będą przetwarzane wyłącznie w celu informowania o przyszłych możliwościach zatrudnienia</li>
                  <li>Podanie danych jest dobrowolne, ale niezbędne do otrzymywania informacji o ofertach pracy</li>
                  <li>Przysługuje mi prawo dostępu, sprostowania, usunięcia, ograniczenia przetwarzania i przenoszenia danych</li>
                  <li>Mogę w dowolnym momencie wycofać zgodę bez wpływu na zgodność z prawem przetwarzania przed jej wycofaniem — wycofanie zgody mogę zgłosić na adres: iod@unitresco.com</li>
                  <li>Po wycofaniu zgody moje dane zostaną usunięte z bazy kandydatów</li>
                  <li>Po upływie 3 lat od złożenia aplikacji moje dane zostaną automatycznie usunięte</li>
                  <li>Przysługuje mi prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych</li>
                  <li>Moje dane nie będą udostępniane podmiotom trzecim, z wyjątkiem podmiotów upoważnionych przepisami prawa</li>
                  <li>W sprawach związanych z ochroną danych mogę kontaktować się pod adresem: iod@unitresco.com</li>
                </ul>
              </Typography>
            </Collapse>
          </div>
          {siteKey ? (
            <ReCAPTCHA sitekey={siteKey} onChange={(v: string | null)=>setCaptchaToken(v)} />
          ) : (
            <Alert severity="error">Brak konfiguracji reCAPTCHA (SITE KEY). Skontaktuj się z administratorem.</Alert>
          )}
          {submitting && <LinearProgress />}
          <Button type="submit" variant="contained" disabled={isPreview || submitting}>{submitting ? 'Wysyłanie…' : 'Wyślij i przejdź dalej'}</Button>
        </Stack>
      </form>
      <Snackbar open={snackOpen} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" sx={{ width: '100%' }}>Przesyłanie plików… prosimy czekać.</Alert>
      </Snackbar>
    </Container>
  );
};

export default CareerVerifyPage;


